import {
  RslReport,
  RslReportsParams,
  RslDimension,
  useCampaign,
  useReport,
  useReports,
  UseReportsProps,
} from '@features/campaigns/subdomains/rsl';
import {
  Campaign,
  GetCampaignParams,
  useReturnOnAdSpend,
} from '@features/campaigns';
import { useGetCostPerVisitCalculationQuery } from '@features/reports';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Subdimension,
  SubdimensionsByName,
  sortByVisitLift,
} from '@features/reports/subdomains/visitation/domain/dimensions';
import { validDimensionSlug } from '@features/reports';
import { TOTAL_DIMENSIONS } from '@features/executive-report/dimensions/layout';
import { EXECUTIVE_REPORT_DIMENSION_SLUG_SET } from '@features/executive-report/domain/dimensions';
import { VisitMetrics } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { ValueInput } from '@utils/formattersV2';

type FetchParams = {
  reportParams: Omit<RslReportsParams, 'campaignId'>;
  campaignParams: GetCampaignParams;
};

const rslDimensionToSubdimension = (dim: RslDimension): Subdimension => ({
  reportId: dim.rslReportId,
  name: dim.dimensionName,
  value: dim.dimensionValue,
  rawValue: dim.dimensionValue,
  statisticallySignificant: dim.statisticallySignificant,
  visitMetrics: {
    optimizationIndex: dim.incrementalVisitLiftIndex ?? null,
    confidenceLevel: dim.incrementalVisitLiftConfidenceLevel ?? null,
    impressions: dim.impressions,
    uniqueUsers: dim.uniqueUsers ?? 0,
    incrementalLift: dim.incrementalVisitLift ?? null,
    incrementalVisits: dim.projectedIncrementalVisits ?? null,
    projectedVisits: dim.totalProjectedVisits ?? null,
    incrementalVisitLiftPercentageIndex: null,
    totalVisitsPerImpressionIndex: dim.totalVisitsPerImpressionIndex ?? null,
    incrementalVisitsPerImpressionIndex:
      dim.incrementalVisitsPerImpressionIndex ?? null,
  },
  salesMetrics: null,
});

const rslReportToVisitMetrics = (report: RslReport): VisitMetrics => ({
  impressions: report.impressions,
  uniqueUsers: report.uniqueUsers ?? 0,
  locationsMeasured: 0,
  incrementalVisits: report.projectedIncrementalVisits ?? null,
  incrementalLift: report.incrementalVisitLift ?? null,
  confidenceLevel: report.incrementalVisitLiftConfidenceLevel ?? null,
  matchedForAttribution: report.matchedForAttribution ?? null,
  projectedVisits: report.totalProjectedVisits ?? null,
  costPerVisit: null,
  costPerIncrementalVisit: null,
  visitCountCumulative: 0,
});

export type UseRslExecutiveReportFieldsHook = {
  loading: boolean;
  data?: {
    campaign: Pick<Campaign, 'name' | 'startDate' | 'endDate' | 'adSpend'>;
    report: RslReport;
    midCampaignReport: RslReport | null;
    iroas: number | null;
    visitMetrics: VisitMetrics;
    topVisitSubdimensionsByName: SubdimensionsByName;
    costPerIncrementalVisit: ValueInput;
  };
  error?: string;
};

export const useExecutiveReportFields = (
  fetchParams: FetchParams
): UseRslExecutiveReportFieldsHook => {
  const { campaign, isFetching: loadingCampaign } = useCampaign(
    fetchParams.campaignParams
  );

  const reportsProps: UseReportsProps = {
    campaignId: campaign?.id,
    campaignParams: fetchParams.campaignParams,
    reportParams: fetchParams.reportParams,
  };
  const { report, loading: loadingReport } = useReport(reportsProps);
  const { reports, loading: loadingReports } = useReports(reportsProps);

  const { value: iroas, isFetching: loadingIroas } = useReturnOnAdSpend({
    campaign: campaign ?? { startDate: '', endDate: '', id: 0 },
    reportDate: report?.reportDate ?? null,
    sales: report?.projectedIncrementalSales ?? null,
    adSpend: campaign?.adSpend ?? null,
  });

  const { data: costPerVisitData, isFetching: loadingCostPerVisit } =
    useGetCostPerVisitCalculationQuery(
      report && report.hasVisitMetrics && campaign
        ? {
            start_date: campaign.startDate,
            end_date: campaign.endDate,
            report_date: report.reportDate,
            visits: report.projectedIncrementalVisits ?? 0,
            ad_spend: campaign.adSpend ?? 0,
          }
        : skipToken
    );

  const loading =
    loadingCampaign ||
    loadingReport ||
    loadingReports ||
    loadingIroas ||
    loadingCostPerVisit;

  const midCampaignReport =
    reports.length > 0
      ? (reports[Math.floor(reports.length / 2)] ?? null)
      : null;

  const visitSubdimensions = (report?.dimensions ?? [])
    .filter(
      d =>
        validDimensionSlug(d.dimensionName) &&
        EXECUTIVE_REPORT_DIMENSION_SLUG_SET.has(d.dimensionName) &&
        d.incrementalVisitLift !== null
    )
    .map(d => rslDimensionToSubdimension(d));

  const groupedByName = visitSubdimensions.reduce((acc, subdimension) => {
    const slug = subdimension.name;
    if (!validDimensionSlug(slug)) return acc;
    acc[slug] = [...(acc[slug] ?? []), subdimension];
    return acc;
  }, {} as SubdimensionsByName);

  const topVisitSubdimensionsByName = Object.entries(groupedByName).reduce(
    (acc, [slug, subdimensions]) => {
      if (!validDimensionSlug(slug)) return acc;
      acc[slug] = subdimensions
        .toSorted((a, b) => sortByVisitLift(a, b))
        .slice(0, TOTAL_DIMENSIONS);
      return acc;
    },
    {} as SubdimensionsByName
  );

  if (!campaign || !report) {
    return { error: 'Missing required data for PPT generation', loading };
  }

  const visitMetrics = rslReportToVisitMetrics(report);
  const costPerIncrementalVisit =
    costPerVisitData?.data?.cost_per_visit ?? null;

  return {
    data: {
      campaign,
      report,
      midCampaignReport,
      iroas,
      visitMetrics,
      topVisitSubdimensionsByName,
      costPerIncrementalVisit,
    },
    loading,
  };
};
